import { useMutation, useQueryClient } from '@tanstack/react-query';

import envVars from '../../../constants/envVars';
import { useSetNotification } from '../../../context/Notification';
import useRequest from '../../../hooks/useRequest';
import { CARE_SOURCE, careErrorText } from '../constants';
import {
    CodeActionPayload,
    CareSuccess,
    ImportSummary,
    WriteReport
} from '../types';
import {
    CARE_HISTORY_KEY,
    CARE_MONTH_KEY,
    CARE_READINESS_KEY,
    CARE_ROWS_KEY,
    CARE_WRITE_KEY,
    careBillingUrl
} from './constants';
import useCareScope from './useCareScope';

const useCareWrite = <TInput>(
    month: string,
    path: string,
    successMessage: string,
    body: (input: TInput) => object | FormData,
    recalculateAfterSave = false,
    onCalculationResult?: (error?: string) => void
) => {
    const req = useRequest();
    const queryClient = useQueryClient();
    const setNotification = useSetNotification();
    const orgId = useCareScope();

    const refresh = () =>
        Promise.all([
            ...[CARE_MONTH_KEY, CARE_ROWS_KEY, CARE_HISTORY_KEY].map((key) =>
                queryClient.invalidateQueries({ queryKey: [key, orgId, month] })
            ),
            queryClient.resetQueries({ queryKey: [CARE_READINESS_KEY, orgId, month] })
        ]);
    const request = (input: TInput) => {
        const content = body(input);
        if (content instanceof FormData) {
            content.set('hero_organization_id', orgId ?? '');
            content.set('month', month);
        }
        return req<CareSuccess<WriteReport & Partial<ImportSummary>>>({
            url: `${envVars.API_RTM_HOSTNAME}${careBillingUrl(path)}`,
            method: 'POST',
            body:
                content instanceof FormData
                    ? content
                    : JSON.stringify({ hero_organization_id: orgId, month, ...content })
        }).then((response) => response.data);
    };

    return useMutation({
        mutationKey: [CARE_WRITE_KEY, orgId, month],
        retry: false,
        mutationFn: request,
        onSuccess: async (report) => {
            try {
                if (recalculateAfterSave && report.outcome !== 'unchanged') {
                    await req<CareSuccess<WriteReport>>({
                        url: `${envVars.API_RTM_HOSTNAME}${careBillingUrl('month/qualify/')}`,
                        method: 'POST',
                        body: JSON.stringify({
                            hero_organization_id: orgId,
                            month,
                            source: CARE_SOURCE
                        })
                    });
                    onCalculationResult?.();
                }
                setNotification({ message: successMessage });
            } catch (error) {
                const message = `Your change saved, but billing results could not update. ${careErrorText(error)}`;
                onCalculationResult?.(message);
                setNotification({
                    message,
                    type: 'fail'
                });
            }
            await refresh();
        },
        onError: (error) => {
            setNotification({ message: careErrorText(error), type: 'fail' });
            // A refused form can have stale tokens. Refresh, but never auto-submit it again.
            refresh();
        }
    });
};

export const usePopulateMonth = (month: string) =>
    useCareWrite<void>(month, 'month/populate/', 'Patients synced.', () => ({}));

export const useQualifyMonth = (month: string) =>
    useCareWrite<void>(month, 'month/qualify/', 'Billable codes calculated.', () => ({
        source: CARE_SOURCE
    }));

export const useCareAction = (month: string, successMessage: string, onCalculationResult?: (error?: string) => void) =>
    useCareWrite<CodeActionPayload>(
        month,
        'month/actions/',
        successMessage,
        (payload) => payload,
        true,
        onCalculationResult
    );

export const useImportMinutes = (month: string) =>
    useCareWrite<{ file: File; rematch: boolean }>(
        month,
        'month/imports/',
        'Minutes import processed.',
        ({ file, rematch }) => {
            const data = new FormData();
            data.set('file', file);
            data.set('source', CARE_SOURCE);
            data.set('rematch', String(rematch));
            return data;
        }
    );

export const useLockMonth = (month: string) =>
    useCareWrite<{ reason: string }>(month, 'month/lock/', 'Month locked.', ({ reason }) => ({
        source: CARE_SOURCE,
        reason
    }));
